import { Button, Popover, Portal, useDisclosure } from "@chakra-ui/react"
import { BlockStyleType } from "@pathwright/blocks-engine"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import { useMemo, useState } from "react"
import styled from "styled-components"
import CustomColors from "./CustomColors"
import {
  ColorStyle,
  blockStyleMenuOptions,
  defaultBlockStyles,
  defaultStyleOptions
} from "./base-style-defaults"
import {
  DropdownButton,
  StyleItem,
  StyleMenu,
  StyleMenuWrapper,
  StyleSwitcherContainer,
  StyleToolWrapper,
  ToolHeader,
  offBlack
} from "./ui"
import {
  getCustomColors,
  getStylesForColorChange,
  getStylesForOption,
  setCustomColors
} from "./utils"

const StyledDropdownButton = styled(DropdownButton)`
  min-height: 20px;
`

type BlockStylesMenuProps = {
  onChangeStyles: (styles: BlockStyleType) => void
  currentStyles?: BlockStyleType
}

const BlockStylesMenu = (props: BlockStylesMenuProps) => {
  const { currentStyles, onChangeStyles } = props

  const disclosure = useDisclosure()
  const [customColorsOpen, setCustomColorsOpen] = useState(false)

  const blockStyles = useMemo(
    () => currentStyles || defaultBlockStyles,
    [currentStyles]
  )

  // Reference structure in `./base-style-defaults.js`
  const handleChangeStyles = (toolKey: string, option: string) => () => {
    const nextStyles = getStylesForOption(toolKey, option, blockStyles)

    onChangeStyles(nextStyles)
  }

  const handleChangeCustomColor = (nextCustomColors: ColorStyle) => {
    // Persist custom colors in local storage for MVP version
    setCustomColors(nextCustomColors)

    const nextStyles = getStylesForColorChange(nextCustomColors, blockStyles)

    setCustomColorsOpen(false)
    onChangeStyles(nextStyles)
  }

  const handleClickCustomColor =
    (hasCustomColors: boolean, customColors: ColorStyle) => () => {
      // If no custom colors exist, we should open the color picker
      if (!hasCustomColors) return setCustomColorsOpen(true)

      // Otherwise, we should choose the existing color scheme
      handleChangeCustomColor(customColors)
    }

  const getColors = () => {
    // Use stored custom colors if they exist

    const colorsInStorage = getCustomColors()

    if (
      colorsInStorage &&
      (colorsInStorage.color || colorsInStorage.backgroundColor)
    )
      return colorsInStorage

    // If no stored colors, return defaults
    const stylesSource = defaultStyleOptions
    return stylesSource.block.color.custom
  }

  const getHasCustomColors = (customColors: ColorStyle) => {
    return customColors
      ? !!customColors.color || !!customColors.backgroundColor
      : false
  }

  const handleClearCustomColors = () => {
    setCustomColorsOpen(false)
    handleChangeStyles("blockColor", "lightest")()
  }

  const { selectedSettings } = blockStyles

  // Currently we have only the block styles menu;
  // will need to refactor if/when we add other menu options (ex: animation)
  const { tools } = blockStyleMenuOptions.block

  const customColors = getColors()
  const hasCustomColors = getHasCustomColors(customColors)

  if (!selectedSettings) {
    console.error("No selected settings", { blockStyles })
    return null
  }

  const handleOpenChange = (e: { open: boolean }) => {
    if (e.open) {
      disclosure.onOpen()
    } else {
      // When closing the blocks styles menu, close the color picker menu in case it's open.
      setCustomColorsOpen(false)
      disclosure.onClose()
    }
  }

  return (
    <Popover.Root
      open={disclosure.open}
      positioning={{
        placement: "bottom-start",
        offset: { crossAxis: -5 }
      }}
      closeOnInteractOutside
      closeOnEscape
      lazyMount
      onOpenChange={handleOpenChange}
    >
      <Tooltip
        title={
          !disclosure.open ? "Customize the look of this block" : undefined
        }
      >
        <Popover.Trigger asChild>
          <StyledDropdownButton>
            <Pathicon icon="brush-2" />
          </StyledDropdownButton>
        </Popover.Trigger>
      </Tooltip>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            p={0}
            bg="transparent"
            boxShadow="none"
            border="none"
          >
            <Popover.Body p={0}>
              <StyleSwitcherContainer>
                {customColorsOpen ? (
                  <CustomColors
                    color={customColors.color}
                    backgroundColor={customColors.backgroundColor}
                    onGoBack={() => setCustomColorsOpen(false)}
                    onClearCustomColors={handleClearCustomColors}
                    onSubmitColors={handleChangeCustomColor}
                    hasCustomColors={hasCustomColors}
                  />
                ) : (
                  <StyleMenuWrapper>
                    {tools.map((tool, i) => (
                      <StyleToolWrapper key={tool.key}>
                        <ToolHeader>
                          <span>{tool.label}</span>
                        </ToolHeader>
                        <StyleMenu
                          {...(tool.key === "blockWidth" ||
                          tool.key === "blockHeight"
                            ? {
                                border: "1px solid rgba(0,0,0,0.2)",
                                p: 0,
                                borderRadius: "7px",
                                overflow: "hidden"
                              }
                            : hasCustomColors
                            ? { mb: "5px" }
                            : {})}
                        >
                          {tool.options.map((toolOption: string, i: number) => {
                            const selected =
                              selectedSettings[tool.key] === toolOption

                            const style =
                              tool.key === "blockColor"
                                ? {
                                    ...tool.handler(toolOption),
                                    boxShadow: selected
                                      ? "inset 0 0 0 2px white, 0 0 0 2px rgba(100,100,100)"
                                      : "",
                                    borderRadius: "7px",
                                    margin: "0 8px 0 0",
                                    padding: "5px 10px",
                                    border:
                                      toolOption === "lightest"
                                        ? "solid 1px #efefef"
                                        : ""
                                  }
                                : {
                                    borderRight:
                                      i < tools.length - 1
                                        ? "1px solid lightgray"
                                        : ""
                                  }

                            if (toolOption === "custom") {
                              return (
                                <div
                                  style={{
                                    borderLeft: "2px solid lightgray"
                                  }}
                                  key={i}
                                >
                                  <StyleItem
                                    className="BlocksStyles__StyleItem"
                                    $selected={selected}
                                    $applyHoverShadows={true}
                                    style={{
                                      ...style,
                                      marginLeft: "10px",
                                      width: "auto",
                                      color: customColors.color,
                                      border: !hasCustomColors
                                        ? "1px solid lightgray"
                                        : "",
                                      backgroundColor:
                                        customColors.backgroundColor
                                    }}
                                    onClick={handleClickCustomColor(
                                      hasCustomColors,
                                      customColors
                                    )}
                                  >
                                    {!hasCustomColors ? (
                                      <Pathicon icon="plus-circle" />
                                    ) : (
                                      "T"
                                    )}
                                  </StyleItem>
                                </div>
                              )
                            }

                            return (
                              <StyleItem
                                key={i}
                                $selected={selected}
                                style={style}
                                $applyHoverShadows={tool.key === "blockColor"}
                                onClick={handleChangeStyles(
                                  tool.key,
                                  toolOption
                                )}
                              >
                                {tool.icons && (
                                  <Pathicon
                                    icon={
                                      tool.icons[
                                        toolOption as keyof typeof tool.icons
                                      ]
                                    }
                                  />
                                )}
                                {tool.type === "color" && "T"}
                              </StyleItem>
                            )
                          })}
                        </StyleMenu>
                        {tool.key === "blockColor" && hasCustomColors && (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "flex-end"
                            }}
                          >
                            <Button
                              onClick={() => setCustomColorsOpen(true)}
                              // @ts-expect-error: custom platform button variant
                              variant="link"
                              color={offBlack}
                              size="sm"
                              gap={0.5}
                              p={0}
                              h="auto"
                            >
                              <Pathicon icon="edit" /> edit
                            </Button>
                          </div>
                        )}
                      </StyleToolWrapper>
                    ))}
                  </StyleMenuWrapper>
                )}
              </StyleSwitcherContainer>
            </Popover.Body>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

export default BlockStylesMenu
